/**
 * HandScaleService — adaptive fit-to-width scaling for the card rows.
 *
 * The hand row has fixed side columns (Buy Rune / Ability Info plates on
 * the left, Buy Card / Ready plates on the right). On a narrow landscape
 * phone the fixed-size hand cards would overflow the frame — anything past
 * ~4-5 cards gets pushed out of view. computeHandCardScale derives the
 * LARGEST uniform scale (≤ the caller's maxScale) at which all current
 * cards fit the available width. The CSS then zooms every card (and the
 * inter-card gap) by that factor via the row's custom property:
 * `zoom` magnifies the whole card subtree uniformly, so the 5:7 aspect
 * ratio, the corner stat badges and every font size scale proportionally.
 *
 * DESKTOP vs MOBILE (task 185): both scale UP to CARD_MAX_SCALE, but
 * DESKTOP fits the REFERENCE count (the strip size is constant while
 * cards come and go) while MOBILE (isMobileLayout — the ≤768px band + the
 * compact landscape band) fits the ACTUAL card count: a lone card scales
 * up to fill the hand, a full hand scales down to fit (aspect ratio kept
 * by the uniform `zoom`). The battlefield (preparation board) and combat
 * rows reuse the same math and mechanism (their constants and property
 * names below).
 *
 * Pure math — no DOM access — so it is unit-testable in isolation.
 */

/** Base hand-card width at scale 1 (px) — desktop `.hand-card`. */
export const HAND_CARD_BASE_WIDTH = 100;

/** Base inter-card gap at scale 1 (px) — desktop `#hand`. */
export const HAND_CARD_BASE_GAP = 22;

/**
 * REFERENCE card count for the hand fit: the hand's MAX (8 cards).
 * The scale is derived from THIS count — never the current card count —
 * so the strip height (which scales with the zoom) is constant per
 * viewport: playing/selling cards never resizes the hand band. A full
 * 8-card hand fills the row exactly; fewer cards sit centered at the
 * same (large) size.
 */
export const HAND_CARD_REF_COUNT = 8;

/**
 * Hand-row FILL factor (playtest: "on desktop cards can be a bit smaller
 * in the hand"): the hand fit targets 90% of the available row width,
 * so a full hand renders ~10% smaller than an exact edge-to-edge fill.
 * The board and combat rows keep the exact 1.0 fill — only the hand is
 * deliberately a touch smaller (it is the densest row and the cards there
 * are read most often). Applied in main.ts as `available * FIT_FACTOR`.
 */
export const HAND_CARD_FIT_FACTOR = 0.9;

/**
 * Readability floor: never scale below this. Below it the corner stat
 * badges and keyword text become unusable, so on extremely narrow screens
 * we accept overflow rather than unreadable cards.
 */
export const HAND_CARD_MIN_SCALE = 0.4;

/**
 * Custom property name published on #hand-area (consumed by the CSS `zoom`
 * on .hand-card, the calc() gap/height on #hand and the calc() bottom
 * padding on #hand-area). Unitless number, 1 = normal.
 */
export const HAND_CARD_SCALE_PROPERTY = '--hand-card-scale';

/**
 * Custom property name published on #hand-area for the hand STRIP height
 * (task 188 — "the hand should be a static height and not be affected by
 * the nr of cards it is holding"): the strip's calc() height consumes
 * THIS variable, not --hand-card-scale. main.ts publishes the MOBILE
 * scale-UP CAP (the 5-card fit — the "1 card in the hand" size) for mobile
 * layouts and the (already constant) reference-fit scale for desktop, so
 * the strip keeps the 1-card height at every card count — including the
 * EMPTY hand (the n=0 → identity scale-1 reset used to balloon the strip
 * back to its base height). Unitless number, 1 = normal.
 */
export const HAND_STRIP_SCALE_PROPERTY = '--hand-strip-scale';

/**
 * Desktop scale-up cap: on a desktop viewport the rows zoom UP so that a
 * full row (7 cards) fills the available width. 2.0 = the 100px hand/
 * board cards at 200px and the 80px combat cards at 160px — large, but
 * never silly on a 4K screen (where the raw fit scale would be 5+).
 * Mobile geometry (70px/56px base cards) always caps at 1 — the mobile
 * layout is unchanged by design.
 *
 * 1.8 (task 176→177 playtest: "the cards on computer is a bit TOO large,
 * it gets uncomfortable — scale them down ~10%"): the cap was 2.0; 2.0 ×
 * 0.9 = 1.8. MOBILE rows (task 185) may scale UP by the ACTUAL card
 * count — but BOUNDED (task 187): the mobile hand cap is the fit scale of
 * MOBILE_HAND_MAX_FIT_COUNT cards and the mobile board cap the fit scale
 * of BOARD_CARD_REF_COUNT (7) cards, so a lone mobile card never grows
 * past the "5-card hand" / "7-card board" size; the desktop/mobile split
 * is the viewport gate in main.ts (isMobileLayout), not this constant.
 */
export const CARD_MAX_SCALE = 1.8;

/**
 * MOBILE hand scale-up bound (task 187 — playtest: "the cards on mobile
 * are now WAY TOO LARGE in the hand / on the battlefield"): on a mobile
 * viewport a hand with FEWER cards than this may scale up, but NEVER past
 * the size the cards have when this many (5) cards are in the hand — that
 * size is the mobile hand's MAXIMUM card size. main.ts computes it as the
 * fit scale of 5 cards (computeHandCardScale(available, base, gap, 5,
 * CARD_MAX_SCALE)) and uses it as the cap for the actual-count fit.
 */
export const MOBILE_HAND_MAX_FIT_COUNT = 5;

/**
 * The maxScale argument for computeHandCardScale, derived from the LIVE
 * (unzoomed) base card width the caller just measured: the desktop
 * geometry gets the scale-up cap, the mobile geometry (70px fullscreen /
 * 56px coop-wide cards) stays capped at 1 — so a narrow desktop window
 * that renders the mobile card sizes (the max-width:768px media query)
 * keeps today's exact mobile behavior.
 */
export function maxScaleForBaseWidth(
  baseWidth: number,
  desktopBaseWidth: number
): number {
  // ±1px tolerance: callers derive baseWidth from an INTEGER pixel width
  // (offsetWidth ÷ scale), so it can land a fraction of a px BELOW the
  // exact base (e.g. round(100×1.24)⁄1.24 = 99.94). A hard >= gate would
  // flip the row to the cap (1) on those ticks and oscillate the scale
  // large/small on every re-measure.
  //
  // TASK 185: this gate only feeds the DESKTOP branch anymore — the
  // mobile rows (isMobileLayout in main.ts) pass CARD_MAX_SCALE directly,
  // so the 70px/56px mobile bases scale UP on mobile like everything
  // else. The `1` branch is now a narrow-desktop-window fallback.
  return baseWidth >= desktopBaseWidth - 1 ? CARD_MAX_SCALE : 1;
}

/**
 * Largest uniform scale (≥ HAND_CARD_MIN_SCALE, ≤ `maxScale`) at which
 * `count` cards, each `baseWidth` px wide and separated by `baseGap` px
 * gaps, fit into `available` px of row width.
 *
 * The gaps scale with the cards (the stat-badge overhang that the gap
 * makes room for shrinks proportionally), so the fit equation is
 * `scale * (count * baseWidth + (count - 1) * baseGap) <= available`.
 *
 * With `maxScale` at its default of 1 the function NEVER upscales
 * (the historical mobile-only behavior). Desktop call sites pass
 * CARD_MAX_SCALE so a full row fills the viewport width (see
 * maxScaleForBaseWidth for the desktop/mobile gate). SUB-ONE caps are
 * honored too (task 187): the mobile hand/board max sizes are the
 * 5-card / 7-card fit scales, which are < 1 on small phones — the cap
 * binds below the base size, so a lone card never grows past them.
 *
 * Degenerate inputs (hidden row / no cards / non-finite numbers) yield
 * the neutral identity scale 1 — the caller keeps whatever scale is
 * already applied.
 *
 * @param available Available row width in px (0 when the row is hidden).
 * @param baseWidth Unzoomed card width in px (100 desktop / 70 mobile
 *   fullscreen — measured live, so media-query changes are picked up).
 * @param baseGap Unzoomed inter-card gap in px (22 desktop / 14 mobile
 *   fullscreen — measured live).
 * @param count Number of cards currently in the row (0 = empty).
 * @param maxScale Upper bound of the scale (default 1 = never upscale).
 * @returns The uniform scale factor to apply (1 = normal size).
 */
export function computeHandCardScale(
  available: number,
  baseWidth: number,
  baseGap: number,
  count: number,
  maxScale: number = 1
): number {
  if (
    !Number.isFinite(available) ||
    !Number.isFinite(baseWidth) ||
    !Number.isFinite(baseGap) ||
    !Number.isFinite(maxScale)
  ) {
    return 1;
  }
  if (count <= 0 || available <= 0 || baseWidth <= 0) {
    return 1;
  }
  const gap = Math.max(0, baseGap);
  const total = count * baseWidth + (count - 1) * gap;
  if (!(total > 0)) {
    return 1;
  }
  // Task 187: the cap may be SUB-ONE (the mobile 5-card/7-card fit bounds)
  // — it binds below the base size too. (The old Math.max(1, …) floor
  // made sub-one caps silently no-op.)
  const cap = Math.max(0, maxScale);
  return Math.min(cap, Math.max(HAND_CARD_MIN_SCALE, available / total));
}

/* ─── Battlefield (preparation board) row scaling ─────────────────────────
   The board row uses the same zoom mechanism as the hand: main.ts
   publishes --board-card-scale on #battlefield and the CSS zooms every
   .battlefield-minion (and the inter-card gap) by that factor. Same fit
   math — a full 7-card board fills the viewport width on desktop. */

/** Base board-minion width at scale 1 (px) — desktop `.battlefield-minion`. */
export const BOARD_CARD_BASE_WIDTH = 100;

/** Base inter-card gap at scale 1 (px) — desktop `#battlefield`. */
export const BOARD_CARD_BASE_GAP = 22;

/**
 * REFERENCE card count for the board fit: the board's max
 * (GameConfig.maxBoardSize = 7). See HAND_CARD_REF_COUNT — the scale is
 * viewport-derived (constant per viewport), a full 7-card board fills the
 * row, fewer cards stay centered at the same size.
 */
export const BOARD_CARD_REF_COUNT = 7;

/**
 * Custom property name published on #battlefield (consumed by the CSS
 * `zoom` on .battlefield-minion and the calc() gap on #battlefield).
 * Unitless number, 1 = normal.
 */
export const BOARD_CARD_SCALE_PROPERTY = '--board-card-scale';

/* ─── Combat board row scaling ────────────────────────────────────────────
   Same mechanism: main.ts publishes --combat-card-scale on #combat-board;
   the CSS zooms every .combat-minion (and the .combat-minions gap). The
   fit uses the LARGEST row (COOP rows carry 10 minions) so a wide row
   never overflows. */

/** Base combat-minion width at scale 1 (px) — desktop `.combat-minion`. */
export const COMBAT_CARD_BASE_WIDTH = 80;

/** Base inter-card gap at scale 1 (px) — desktop `.combat-minions`. */
export const COMBAT_CARD_BASE_GAP = 22;

/**
 * REFERENCE card count for the combat fit: a normal combat row's max
 * (7). The fit uses max(REF, actual row count) — a COOP 10-wide row gets
 * the milder 10-fit so it never overflows, and the scale is constant
 * during the combat (cards dying mid-combat do NOT re-scale the row).
 */
export const COMBAT_CARD_REF_COUNT = 7;

/**
 * Custom property name published on #combat-board (consumed by the CSS
 * `zoom` on .combat-minion and the calc() gap on .combat-minions).
 * Unitless number, 1 = normal.
 */
export const COMBAT_CARD_SCALE_PROPERTY = '--combat-card-scale';
